// APIStore.js - Sykes Anaesthetics v14.6 Security Hardened
// SEC-15: Generic error messages in production; detailed in development

const API_BASE = 'https://sykessedation.co.uk/api';
const IS_PROD = typeof import.meta !== 'undefined' && import.meta.env?.PROD === true;

function logError(context, error) {
  if (IS_PROD) {
    console.error(`[APIStore] ${context} failed`);
  } else {
    console.error(`[APIStore] ${context}:`, error.message);
  }
}

// ── Token refresh handling ──────────────────────────────────────────────────
let _refreshPromise = null; // deduplicate concurrent refresh attempts
let _onTokenRefreshed = null; // callback to update portal token state

export function setTokenRefreshCallback(cb) {
  _onTokenRefreshed = cb;
}

async function refreshToken(currentToken) {
  // Deduplicate: if a refresh is already in flight, wait for it
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (data.token && _onTokenRefreshed) {
        _onTokenRefreshed(data.token);
      }
      return data.token || null;
    } catch(e) {
      logError('refreshToken', e);
      return null;
    } finally {
      _refreshPromise = null;
    }
  })();
  return _refreshPromise;
}

// Wrapper: fetch with auto-refresh on 401 TOKEN_EXPIRED
async function authFetch(url, options = {}, tokenOverride) {
  const token = tokenOverride || options.headers?.Authorization?.replace('Bearer ', '');
  const res = await fetch(url, options);
  if (res.status === 401) {
    try {
      const body = await res.clone().json();
      if (body.code === 'TOKEN_EXPIRED' && token) {
        const newToken = await refreshToken(token);
        if (newToken) {
          // Retry with new token
          const newOptions = {
            ...options,
            headers: { ...options.headers, Authorization: `Bearer ${newToken}` }
          };
          return fetch(url, newOptions);
        }
      }
    } catch(e) { /* parse error, return original response */ }
  }
  return res;
}

export const APIStore = {

  // ── PATIENTS ──────────────────────────────────────────────────────────────

  async loadPatients(token) {
    try {
      const res = await authFetch(`${API_BASE}/patients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      const rows = await res.json();
      return rows.map(row => {
        // row.data may arrive as a JSON string (from server-side encryption/serialisation)
        // or as an already-parsed object. Normalise to an object before spreading.
        let dataObj = {};
        if (typeof row.data === 'string') {
          try { dataObj = JSON.parse(row.data); } catch (e) { /* malformed */ }
        } else if (row.data && typeof row.data === 'object') {
          dataObj = row.data;
        }
        return {
          ...dataObj,
          crn: row.crn,
          full_name: row.full_name,
          patientName: row.full_name || dataObj.patientName || dataObj.name || '',
          dob: row.dob || dataObj.dob || '',
          submittedAt: row.created_at || dataObj.submittedAt || '',
          adminStatus: row.admin_status || dataObj.adminStatus || 'new_registration',
          _dbId: row.id,
          // BUG-06 Phase D Step 3: prefer the canonical integer clinic_fk_id as the
          // tenant key; legacy varchar clinic_id kept only as a fallback for old rows.
          _clinicId: row.clinic_fk_id != null ? row.clinic_fk_id : row.clinic_id,
          _clinicFkId: row.clinic_fk_id != null ? row.clinic_fk_id : null,
          _anaesthetistId: row.anaesthetist_id,
          _adminStatus: row.admin_status,
        };
      });
    } catch (e) {
      logError('loadPatients', e);
      return [];
    }
  },

  async savePatient(token, patient) {
    try {
      const res = await authFetch(`${API_BASE}/patients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          crn: patient.crn,
          full_name: patient.name || patient.full_name || '',
          dob: patient.dob || null,
          // BUG-06 Phase D Step 3: legacy clinic_id write-back removed. For clinic-role
          // saves the server stamps the tenant from req.user.clinic_fk_id (session), not
          // the request body; the legacy varchar column is no longer sent from the client.
          data: patient
        })
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('savePatient', e);
      return null;
    }
  },

  async bulkSavePatients(token, patients) {
    try {
      const res = await authFetch(`${API_BASE}/patients/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ patients })
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('bulkSavePatients', e);
      return null;
    }
  },

  async updatePatient(token, crn, updates) {
    try {
      const res = await authFetch(`${API_BASE}/patients/${crn}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('updatePatient', e);
      return null;
    }
  },

  // ── SESSIONS ──────────────────────────────────────────────────────────────
  // Removed 19 Apr 2026: loadSessions / saveSession / updateSession were vestigial —
  // no client callers anywhere, and /api/sessions has no server handler. If session
  // management is needed in future, add the server endpoint first, then reintroduce
  // here using the parseRowData pattern used in loadPatients.

  // ── USERS ─────────────────────────────────────────────────────────────────

  async loadUsers(token) {
    try {
      const res = await authFetch(`${API_BASE}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('loadUsers', e);
      return [];
    }
  },

  // ── BOOKING PIPELINE ──────────────────────────────────────────────────────

  async loadPipeline(token) {
    try {
      const res = await authFetch(`${API_BASE}/pipeline`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('loadPipeline', e);
      return [];
    }
  },

  async savePipelineBooking(token, booking) {
    try {
      const res = await authFetch(`${API_BASE}/pipeline`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          clinicRegId: booking.clinicRegId,
          crn: booking.crn,
          status: booking.status || 'new_registration',
          data: booking
        })
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('savePipelineBooking', e);
      return null;
    }
  },

  async updatePipelineStatus(token, clinicRegId, status, extraData, anaesthetistId, overrideOptions = null) {
    try {
      const res = await authFetch(`${API_BASE}/pipeline/${clinicRegId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          status,
          data: extraData || {},
          anaesthetist_id: anaesthetistId,
          // P4-04 + FU8: allocation override fields, only sent when admin has
          // typed a justification in the booking modal blocker banner.
          ...(overrideOptions
            ? { override: !!overrideOptions.override, override_reason: overrideOptions.reason || '' }
            : {})
        })
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('updatePipelineStatus', e);
      return null;
    }
  },

  async deletePipelineBooking(token, clinicRegId) {
    try {
      const res = await authFetch(`${API_BASE}/pipeline/${clinicRegId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('deletePipelineBooking', e);
      return null;
    }
  },

  // ── REVIEW QUEUE ──────────────────────────────────────────────────────────

  async loadReviews(token) {
    try {
      const res = await authFetch(`${API_BASE}/reviews`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('loadReviews', e);
      return [];
    }
  },

  async addReview(token, review) {
    try {
      const res = await authFetch(`${API_BASE}/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          clinicRegId: review.clinicRegId,
          crn: review.crn,
          anaesthetist_id: review.anaesthetist_id,
          data: review
        })
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('addReview', e);
      return null;
    }
  },

  async updateReview(token, clinicRegId, status, extraData) {
    try {
      const res = await authFetch(`${API_BASE}/reviews/${clinicRegId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          status,
          data: extraData || {}
        })
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('updateReview', e);
      return null;
    }
  },

  // BUG-03: atomic review submission — single endpoint that wraps DB updates
  // (booking_pipeline + review_queue + reviews + audit_log) in a transaction,
  // then fires emails + admin notification best-effort. Returns rich status:
  //   { committed, decision, notifications: {patientEmail, adminEmail, adminNotification}, errors[] }
  // Replaces the previous multi-call sequence in submitReview.
  async submitReview(token, clinicRegId, payload) {
    try {
      const res = await authFetch(`${API_BASE}/reviews/${clinicRegId}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload || {})
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        return { committed: false, error: data.error || ('HTTP ' + res.status), notifications: {}, errors: [] };
      }
      return data;
    } catch (e) {
      logError('submitReview', e);
      return { committed: false, error: e.message || 'Network error', notifications: {}, errors: [] };
    }
  },

  // ── NOTIFICATIONS ─────────────────────────────────────────────────────────

  async loadNotifications(token) {
    try {
      const res = await authFetch(`${API_BASE}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('loadNotifications', e);
      return [];
    }
  },

  async sendNotification(token, notification) {
    try {
      const res = await authFetch(`${API_BASE}/notifications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(notification)
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('sendNotification', e);
      return null;
    }
  },

  // Send email via Resend (calls /api/notifications/send which expects {to, subject, body})
  async sendEmail(token, { to, subject, body, cc }) {
    try {
      const res = await authFetch(`${API_BASE}/notifications/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ to, subject, body, cc })
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('sendEmail', e);
      return null;
    }
  },

  async markNotificationRead(token, id) {
    try {
      const res = await authFetch(`${API_BASE}/notifications/${id}/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('markNotificationRead', e);
      return null;
    }
  },

  async markAllNotificationsRead(token) {
    try {
      const res = await authFetch(`${API_BASE}/notifications/read-all`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('markAllNotificationsRead', e);
      return null;
    }
  },

  // ── PATIENT FILES ─────────────────────────────────────────────────────────

  async uploadFiles(token, crn, files, clinicRegId, category) {
    try {
      const formData = new FormData();
      for (const file of files) formData.append('files', file);
      if (clinicRegId) formData.append('clinic_reg_id', clinicRegId);
      if (category) formData.append('category', category);
      const res = await authFetch(`${API_BASE}/files/${crn}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('uploadFiles', e);
      return null;
    }
  },

  async listFiles(token, crn) {
    try {
      const res = await authFetch(`${API_BASE}/files/${crn}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('listFiles', e);
      return [];
    }
  },

  getFileDownloadUrl(crn, filename) {
    return `${API_BASE}/files/${crn}/${filename}`;
  },

  async deleteFile(token, crn, fileId) {
    try {
      const res = await authFetch(`${API_BASE}/files/${crn}/${fileId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('deleteFile', e);
      return null;
    }
  },

  // ══════════════════════════════════════════════════════════════════════════
  // PHASE 2: PATIENT FORMS
  // ══════════════════════════════════════════════════════════════════════════

  async loadPatientForms(token, crn) {
    try {
      const url = crn ? `${API_BASE}/patient-forms/${crn}` : `${API_BASE}/patient-forms`;
      const res = await authFetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('loadPatientForms', e);
      return [];
    }
  },

  async loadPatientFormsByPortal(token, portal) {
    try {
      const res = await authFetch(`${API_BASE}/patient-forms?portal=${portal}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('loadPatientFormsByPortal', e);
      return [];
    }
  },

  async savePatientForm(token, form) {
    try {
      const res = await authFetch(`${API_BASE}/patient-forms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          crn: form.crn,
          form_id: form.id || form.formId,
          form_type: form.formType || form.form_type,
          form_name: form.formName || form.form_name,
          category: form.cat || form.category,
          portal: form.portal,
          created_by: form.createdBy || form.created_by,
          data: form.data || form
        })
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('savePatientForm', e);
      return null;
    }
  },

  async updatePatientFormField(token, crn, formId, field, value) {
    try {
      const res = await authFetch(`${API_BASE}/patient-forms/${crn}/${formId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ field, value })
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('updatePatientFormField', e);
      return null;
    }
  },

  async bulkSavePatientForms(token, forms) {
    try {
      const res = await authFetch(`${API_BASE}/patient-forms/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ forms })
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('bulkSavePatientForms', e);
      return null;
    }
  },

  // ══════════════════════════════════════════════════════════════════════════
  // PHASE 2: COMPLETED SESSIONS
  // ══════════════════════════════════════════════════════════════════════════

  async loadCompletedSessions(token) {
    try {
      const res = await authFetch(`${API_BASE}/completed-sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('loadCompletedSessions', e);
      return [];
    }
  },

  async saveCompletedSession(token, session) {
    try {
      const res = await authFetch(`${API_BASE}/completed-sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          crn: session.crn,
          sid: session.sid || session.session?.sid,
          anaesthetist_name: session.completedBy || session.anaesthetist_name,
          data: session
        })
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('saveCompletedSession', e);
      return null;
    }
  },

  async bulkSaveCompletedSessions(token, sessions) {
    try {
      const res = await authFetch(`${API_BASE}/completed-sessions/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ sessions })
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('bulkSaveCompletedSessions', e);
      return null;
    }
  },

  // ══════════════════════════════════════════════════════════════════════════
  // PHASE 2: CLINICS
  // ══════════════════════════════════════════════════════════════════════════

  async loadClinics(token) {
    try {
      const res = await authFetch(`${API_BASE}/clinics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('loadClinics', e);
      return [];
    }
  },

  async saveClinic(token, clinic) {
    try {
      const res = await authFetch(`${API_BASE}/clinics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          clinic_key: clinic.id || clinic.clinic_key,
          name: clinic.name,
          data: clinic
        })
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('saveClinic', e);
      return null;
    }
  },

  async bulkSaveClinics(token, clinics) {
    try {
      const res = await authFetch(`${API_BASE}/clinics/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ clinics })
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('bulkSaveClinics', e);
      return null;
    }
  },

  async deleteClinic(token, clinicKey) {
    try {
      const res = await authFetch(`${API_BASE}/clinics/${encodeURIComponent(clinicKey)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('deleteClinic', e);
      return null;
    }
  },

  // ══════════════════════════════════════════════════════════════════════════
  // PHASE 2: ANAESTHETIST RECORDS (SCR)
  // ══════════════════════════════════════════════════════════════════════════

  async loadAnaesthetistRecords(token) {
    try {
      const res = await authFetch(`${API_BASE}/anaesthetist-records`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('loadAnaesthetistRecords', e);
      return [];
    }
  },

  async saveAnaesthetistRecord(token, record) {
    try {
      const res = await authFetch(`${API_BASE}/anaesthetist-records`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          record_id: record.id,
          name: record.personal?.name || record.name,
          data: record
        })
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('saveAnaesthetistRecord', e);
      return null;
    }
  },

  async bulkSaveAnaesthetistRecords(token, records) {
    try {
      const res = await authFetch(`${API_BASE}/anaesthetist-records/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ records })
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('bulkSaveAnaesthetistRecords', e);
      return null;
    }
  },

  async deleteAnaesthetistRecord(token, recordId) {
    try {
      const res = await authFetch(`${API_BASE}/anaesthetist-records/${encodeURIComponent(recordId)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('deleteAnaesthetistRecord', e);
      return null;
    }
  },

  // ══════════════════════════════════════════════════════════════════════════
  // PHASE 2: PORTAL STATE
  // ══════════════════════════════════════════════════════════════════════════

  async loadPortalState(token, key) {
    try {
      const res = await authFetch(`${API_BASE}/portal-state/${key}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('loadPortalState', e);
      return null;
    }
  },

  async savePortalState(token, key, data) {
    try {
      const res = await authFetch(`${API_BASE}/portal-state/${key}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ data })
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('savePortalState', e);
      return null;
    }
  },

  async loadAllPortalState(token) {
    try {
      const res = await authFetch(`${API_BASE}/portal-state`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('loadAllPortalState', e);
      return {};
    }
  },

  // ══════════════════════════════════════════════════════════════════════════
  // PHASE 2: ANAESTHETIST AVAILABILITY
  // ══════════════════════════════════════════════════════════════════════════

  async loadAvailability(token) {
    try {
      const res = await authFetch(`${API_BASE}/availability`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('loadAvailability', e);
      return {};
    }
  },

  async saveAvailability(token, recordId, data) {
    try {
      const res = await authFetch(`${API_BASE}/availability/${recordId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ data })
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('saveAvailability', e);
      return null;
    }
  },

  async bulkSaveAvailability(token, availability) {
    try {
      const res = await authFetch(`${API_BASE}/availability`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ availability })
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch (e) {
      logError('bulkSaveAvailability', e);
      return null;
    }
  },

  // ══════════════════════════════════════════════════════════════════════════
  // AUTH: LOGOUT (blacklists token server-side)
  // ══════════════════════════════════════════════════════════════════════════

  async logout(token) {
    try {
      await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch(e) {
      // Logout should always succeed from user perspective
    }
  },

  // ── P1-01: Cookie/storage policy acknowledgement ──────────────────────────
  async getPolicyStatus(token) {
    try {
      const res = await authFetch(`${API_BASE}/auth/policy-status`, {
        headers: { Authorization: `Bearer ${token}` }
      }, token);
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) {
      logError('getPolicyStatus', e);
      return null;
    }
  },

  async acknowledgePolicy(token) {
    try {
      const res = await authFetch(`${API_BASE}/auth/acknowledge-policy`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      }, token);
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) {
      logError('acknowledgePolicy', e);
      return null;
    }
  },

  // ══════════════════════════════════════════════════════════════════════════
  // USER MANAGEMENT (admin only)
  // ══════════════════════════════════════════════════════════════════════════

  async changePassword(token, currentPassword, newPassword) {
    try {
      const res = await authFetch(`${API_BASE}/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      return data;
    } catch(e) {
      logError('changePassword', e);
      throw e;
    }
  },

  async loadManagedUsers(token) {
    try {
      const res = await authFetch(`${API_BASE}/users/managed`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) {
      logError('loadManagedUsers', e);
      return [];
    }
  },

  async createUser(token, user) {
    try {
      const res = await authFetch(`${API_BASE}/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(user)
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.error || 'Failed'); }
      return await res.json();
    } catch(e) {
      logError('createUser', e);
      throw e;
    }
  },

  async updateUser(token, id, updates) {
    try {
      const res = await authFetch(`${API_BASE}/users/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(updates)
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.error || 'Failed'); }
      return await res.json();
    } catch(e) {
      logError('updateUser', e);
      throw e;
    }
  },

  async deleteUser(token, id) {
    try {
      const res = await authFetch(`${API_BASE}/users/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.error || 'Failed'); }
      return await res.json();
    } catch(e) {
      logError('deleteUser', e);
      throw e;
    }
  },

  async reactivateUser(token, id) {
    try {
      const res = await authFetch(`${API_BASE}/users/${id}/reactivate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) {
      logError('reactivateUser', e);
      throw e;
    }
  },

  async resetUserMfa(token, id) {
    try {
      const res = await authFetch(`${API_BASE}/users/${id}/reset-mfa`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) {
      logError('resetUserMfa', e);
      throw e;
    }
  },

  // GOV-21: deliberate, audited admin password reset
  async resetUserPassword(token, id, password) {
    try {
      const res = await authFetch(`${API_BASE}/users/${id}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ password })
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Failed'); }
      return await res.json();
    } catch(e) {
      logError('resetUserPassword', e);
      throw e;
    }
  },

  async deletePatient(token, crn) {
    try {
      const res = await authFetch(`${API_BASE}/patients/${crn}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.error || 'Failed'); }
      return await res.json();
    } catch(e) {
      logError('deletePatient', e);
      throw e;
    }
  },

  async loadPatientsByClinic(token, clinicId) {
    try {
      const res = await authFetch(`${API_BASE}/patients/by-clinic/${encodeURIComponent(clinicId)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) {
      logError('loadPatientsByClinic', e);
      return [];
    }
  },

  async loadPipelineByClinic(token, clinicId) {
    try {
      const res = await authFetch(`${API_BASE}/pipeline/by-clinic/${encodeURIComponent(clinicId)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) {
      logError('loadPipelineByClinic', e);
      return [];
    }
  },

  async loadMyPipeline(token) {
    try {
      const res = await authFetch(`${API_BASE}/pipeline/mine`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) {
      logError('loadMyPipeline', e);
      return [];
    }
  },

  async loadMyPatients(token) {
    try {
      const res = await authFetch(`${API_BASE}/patients/mine`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) {
      logError('loadMyPatients', e);
      return [];
    }
  },

  // ── BATCH INIT — single call replaces 5+ separate API calls on login ──
  async init(token) {
    try {
      const res = await authFetch(`${API_BASE}/init`, {
        headers: { Authorization: `Bearer ${token}` }
      }, token);
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) {
      logError('init', e);
      return null;
    }
  },

  // ── CRN generation (server-side, atomic) ──
  // Returns { crn: 'SA-YYYY-NNNN' } on success, or null on failure
  async nextCRN(token) {
    try {
      const res = await authFetch(`${API_BASE}/crn/next`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      }, token);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      return data.crn || null;
    } catch(e) {
      logError('nextCRN', e);
      return null;
    }
  },

  // ─────────────────────────────────────────────────────────────────────────
  //   P4-02 INVOICING (Session A) — admin-only
  // ─────────────────────────────────────────────────────────────────────────
  // All endpoints live at /api/invoicing/*; see server/invoicing-routes.cjs.
  // Helpers return [] / null on failure (consistent with rest of APIStore).

  // --- Clinic billing profiles (outgoing lane) ---
  async loadClinicBillingProfiles(token) {
    try {
      const res = await authFetch(`${API_BASE}/invoicing/billing-profiles/clinics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) { logError('loadClinicBillingProfiles', e); return []; }
  },
  async createClinicBillingProfile(token, profile) {
    try {
      const res = await authFetch(`${API_BASE}/invoicing/billing-profiles/clinics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(profile)
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.error || 'Failed'); }
      return await res.json();
    } catch(e) { logError('createClinicBillingProfile', e); throw e; }
  },
  async updateClinicBillingProfile(token, id, updates) {
    try {
      const res = await authFetch(`${API_BASE}/invoicing/billing-profiles/clinics/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(updates)
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.error || 'Failed'); }
      return await res.json();
    } catch(e) { logError('updateClinicBillingProfile', e); throw e; }
  },

  // --- Anaesthetist billing profiles (contractor lane) ---
  async loadAnaesthetistBillingProfiles(token) {
    try {
      const res = await authFetch(`${API_BASE}/invoicing/billing-profiles/anaesthetists`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) { logError('loadAnaesthetistBillingProfiles', e); return []; }
  },
  async createAnaesthetistBillingProfile(token, profile) {
    try {
      const res = await authFetch(`${API_BASE}/invoicing/billing-profiles/anaesthetists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(profile)
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.error || 'Failed'); }
      return await res.json();
    } catch(e) { logError('createAnaesthetistBillingProfile', e); throw e; }
  },
  async updateAnaesthetistBillingProfile(token, id, updates) {
    try {
      const res = await authFetch(`${API_BASE}/invoicing/billing-profiles/anaesthetists/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(updates)
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.error || 'Failed'); }
      return await res.json();
    } catch(e) { logError('updateAnaesthetistBillingProfile', e); throw e; }
  },

  // --- Anaesthetist clinic rates ---
  async loadAnaesthetistClinicRates(token, opts = {}) {
    try {
      const params = new URLSearchParams();
      if (opts.anaesthetist_user_id) params.set('anaesthetist_user_id', opts.anaesthetist_user_id);
      if (opts.clinic_fk_id) params.set('clinic_fk_id', opts.clinic_fk_id);
      if (opts.active_only) params.set('active_only', 'true');
      const qs = params.toString();
      const res = await authFetch(`${API_BASE}/invoicing/rates${qs ? '?' + qs : ''}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) { logError('loadAnaesthetistClinicRates', e); return []; }
  },
  async createAnaesthetistClinicRate(token, rate) {
    try {
      const res = await authFetch(`${API_BASE}/invoicing/rates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(rate)
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.error || 'Failed'); }
      return await res.json();
    } catch(e) { logError('createAnaesthetistClinicRate', e); throw e; }
  },
  async updateAnaesthetistClinicRate(token, id, updates) {
    try {
      const res = await authFetch(`${API_BASE}/invoicing/rates/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(updates)
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.error || 'Failed'); }
      return await res.json();
    } catch(e) { logError('updateAnaesthetistClinicRate', e); throw e; }
  },

  // --- Outgoing invoices ---
  async loadOutgoingInvoices(token, filters = {}) {
    try {
      const params = new URLSearchParams(filters);
      const qs = params.toString();
      const res = await authFetch(`${API_BASE}/invoicing/outgoing${qs ? '?' + qs : ''}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) { logError('loadOutgoingInvoices', e); return []; }
  },
  async getOutgoingInvoice(token, id) {
    try {
      const res = await authFetch(`${API_BASE}/invoicing/outgoing/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) { logError('getOutgoingInvoice', e); return null; }
  },
  async updateOutgoingInvoice(token, id, updates) {
    try {
      const res = await authFetch(`${API_BASE}/invoicing/outgoing/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(updates)
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.error || 'Failed'); }
      return await res.json();
    } catch(e) { logError('updateOutgoingInvoice', e); throw e; }
  },
  async aggregateOutgoingInvoice(token, opts) {
    try {
      const res = await authFetch(`${API_BASE}/invoicing/outgoing/aggregate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(opts)
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.error || 'Failed'); }
      return await res.json();
    } catch(e) { logError('aggregateOutgoingInvoice', e); throw e; }
  },
  async sendOutgoingByEmail(token, id, { recipient_email, cc_emails, custom_message } = {}) {
    try {
      const res = await authFetch(`${API_BASE}/invoicing/outgoing/${id}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ recipient_email, cc_emails, custom_message })
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.error || 'Failed'); }
      return await res.json();
    } catch(e) { logError('sendOutgoingByEmail', e); throw e; }
  },
  async markOutgoingSent(token, id, sent_to_email) {
    try {
      const res = await authFetch(`${API_BASE}/invoicing/outgoing/${id}/mark-sent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sent_to_email })
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.error || 'Failed'); }
      return await res.json();
    } catch(e) { logError('markOutgoingSent', e); throw e; }
  },
  async markOutgoingPaid(token, id, payment_reference) {
    try {
      const res = await authFetch(`${API_BASE}/invoicing/outgoing/${id}/mark-paid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ payment_reference })
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.error || 'Failed'); }
      return await res.json();
    } catch(e) { logError('markOutgoingPaid', e); throw e; }
  },

  // --- Contractor invoices ---
  async loadContractorInvoices(token, filters = {}) {
    try {
      const params = new URLSearchParams(filters);
      const qs = params.toString();
      const res = await authFetch(`${API_BASE}/invoicing/contractor${qs ? '?' + qs : ''}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) { logError('loadContractorInvoices', e); return []; }
  },
  async getContractorInvoice(token, id) {
    try {
      const res = await authFetch(`${API_BASE}/invoicing/contractor/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) { logError('getContractorInvoice', e); return null; }
  },
  async updateContractorInvoice(token, id, updates) {
    try {
      const res = await authFetch(`${API_BASE}/invoicing/contractor/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(updates)
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.error || 'Failed'); }
      return await res.json();
    } catch(e) { logError('updateContractorInvoice', e); throw e; }
  },
  async aggregateContractorInvoice(token, opts) {
    try {
      const res = await authFetch(`${API_BASE}/invoicing/contractor/aggregate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(opts)
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.error || 'Failed'); }
      return await res.json();
    } catch(e) { logError('aggregateContractorInvoice', e); throw e; }
  },
  async markContractorSent(token, id) {
    try {
      const res = await authFetch(`${API_BASE}/invoicing/contractor/${id}/mark-sent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.error || 'Failed'); }
      return await res.json();
    } catch(e) { logError('markContractorSent', e); throw e; }
  },
  async markContractorPaid(token, id, payment_reference) {
    try {
      const res = await authFetch(`${API_BASE}/invoicing/contractor/${id}/mark-paid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ payment_reference })
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.error || 'Failed'); }
      return await res.json();
    } catch(e) { logError('markContractorPaid', e); throw e; }
  },

  // --- Polymorphic line CRUD ---
  async addInvoiceLine(token, parentType, parentId, line) {
    try {
      const res = await authFetch(`${API_BASE}/invoicing/${parentType}/${parentId}/lines`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(line)
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.error || 'Failed'); }
      return await res.json();
    } catch(e) { logError('addInvoiceLine', e); throw e; }
  },
  async updateInvoiceLine(token, lineId, updates) {
    try {
      const res = await authFetch(`${API_BASE}/invoicing/lines/${lineId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(updates)
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.error || 'Failed'); }
      return await res.json();
    } catch(e) { logError('updateInvoiceLine', e); throw e; }
  },
  async deleteInvoiceLine(token, lineId) {
    try {
      const res = await authFetch(`${API_BASE}/invoicing/lines/${lineId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.error || 'Failed'); }
      return await res.json();
    } catch(e) { logError('deleteInvoiceLine', e); throw e; }
  },

  // --- Helper: list uninvoiced bookings (for aggregation UI preview) ---
  async loadUninvoicedBookings(token, filters = {}) {
    try {
      const params = new URLSearchParams(filters);
      const qs = params.toString();
      const res = await authFetch(`${API_BASE}/invoicing/uninvoiced-bookings${qs ? '?' + qs : ''}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) { logError('loadUninvoicedBookings', e); return []; }
  },

  // --- DOCX generation (P4-02 Session B) ---
  // Both methods POST to the server, receive a binary blob, and trigger a
  // browser download via an ephemeral <a download> link. The server also
  // archives the file to /var/www/sykes/invoices/ and sets docx_path on
  // the invoice row.
  async generateOutgoingDocx(token, invoiceId) {
    try {
      const res = await authFetch(`${API_BASE}/invoicing/outgoing/${invoiceId}/generate-docx`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to generate DOCX');
      }
      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') || '';
      const m = cd.match(/filename="(.+?)"/);
      const filename = m ? m[1] : ('invoice-' + invoiceId + '.docx');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      return { success: true, filename };
    } catch(e) { logError('generateOutgoingDocx', e); throw e; }
  },

  async generateContractorDocx(token, invoiceId) {
    try {
      const res = await authFetch(`${API_BASE}/invoicing/contractor/${invoiceId}/generate-docx`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to generate DOCX');
      }
      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') || '';
      const m = cd.match(/filename="(.+?)"/);
      const filename = m ? m[1] : ('invoice-' + invoiceId + '.docx');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      return { success: true, filename };
    } catch(e) { logError('generateContractorDocx', e); throw e; }
  },

  // ──────────────────────────────────────────────────────────────────────
  // INVOICING — anaesthetist self-service (P4-02-FU2, 25 May 2026)
  // Read-only. Server gates by anaesthetist role + filters by req.user.id.
  // ──────────────────────────────────────────────────────────────────────

  async getMyBillingProfile(token) {
    try {
      const res = await authFetch(`${API_BASE}/invoicing/my/billing-profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 404) return null;
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) { logError('getMyBillingProfile', e); return null; }
  },

  async getMyRates(token) {
    try {
      const res = await authFetch(`${API_BASE}/invoicing/my/rates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) { logError('getMyRates', e); return []; }
  },

  async getMyContractorInvoices(token, filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.from) params.set('from', filters.from);
      if (filters.to) params.set('to', filters.to);
      const qs = params.toString();
      const res = await authFetch(`${API_BASE}/invoicing/my/contractor${qs ? '?' + qs : ''}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) { logError('getMyContractorInvoices', e); return []; }
  },

  async getMyContractorInvoice(token, invoiceId) {
    try {
      const res = await authFetch(`${API_BASE}/invoicing/my/contractor/${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) { logError('getMyContractorInvoice', e); return null; }
  },

  async downloadMyContractorInvoiceDocx(token, invoiceId) {
    try {
      const res = await authFetch(`${API_BASE}/invoicing/my/contractor/${invoiceId}/docx`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to download DOCX');
      }
      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') || '';
      const m = cd.match(/filename="(.+?)"/);
      const filename = m ? m[1] : ('invoice-' + invoiceId + '.docx');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      return { success: true, filename };
    } catch(e) { logError('downloadMyContractorInvoiceDocx', e); throw e; }
  },

  // ──────────────────────────────────────────────────────────────────────
  // INVOICING REPORTS (P4-02-FU4, 25 May 2026) — admin analytics
  // ──────────────────────────────────────────────────────────────────────

  async getArAgedReport(token, asOf) {
    try {
      const params = new URLSearchParams();
      if (asOf) params.set('as_of', asOf);
      const qs = params.toString();
      const res = await authFetch(`${API_BASE}/invoicing/reports/ar-aged${qs ? '?' + qs : ''}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) { logError('getArAgedReport', e); return null; }
  },

  async getContractorDueReport(token) {
    try {
      const res = await authFetch(`${API_BASE}/invoicing/reports/contractor-due`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) { logError('getContractorDueReport', e); return null; }
  },

  async getMonthlyRevenueReport(token, from, to, clinicFkId) {
    try {
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      if (clinicFkId) params.set('clinic_fk_id', clinicFkId);
      const qs = params.toString();
      const res = await authFetch(`${API_BASE}/invoicing/reports/monthly-revenue${qs ? '?' + qs : ''}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) { logError('getMonthlyRevenueReport', e); return null; }
  },

  async getPlSummaryReport(token, from, to) {
    try {
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      const qs = params.toString();
      const res = await authFetch(`${API_BASE}/invoicing/reports/pl-summary${qs ? '?' + qs : ''}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) { logError('getPlSummaryReport', e); return null; }
  },

  // ──────────────────────────────────────────────────────────────────────
  // SCHEDULING (P4-03) — multi-anaesthetist availability + allocations
  // ──────────────────────────────────────────────────────────────────────

  async loadSchedulingHealth(token) {
    try {
      const res = await authFetch(`${API_BASE}/scheduling/health`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) { logError('loadSchedulingHealth', e); return null; }
  },

  async loadAvailability(token, filters = {}) {
    try {
      const params = new URLSearchParams(filters);
      const qs = params.toString();
      const res = await authFetch(`${API_BASE}/scheduling/availability${qs ? '?' + qs : ''}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      return data.rows || [];
    } catch(e) { logError('loadAvailability', e); return []; }
  },

  async createAvailability(token, row) {
    try {
      const res = await authFetch(`${API_BASE}/scheduling/availability`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(row)
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.error || 'Failed'); }
      const data = await res.json();
      return data.row;
    } catch(e) { logError('createAvailability', e); throw e; }
  },

  async bulkCreateAvailability(token, payload) {
    try {
      const res = await authFetch(`${API_BASE}/scheduling/availability/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.error || 'Failed'); }
      return await res.json();
    } catch(e) { logError('bulkCreateAvailability', e); throw e; }
  },

  async updateAvailability(token, id, patch) {
    try {
      const res = await authFetch(`${API_BASE}/scheduling/availability/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(patch)
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.error || 'Failed'); }
      const data = await res.json();
      return data.row;
    } catch(e) { logError('updateAvailability', e); throw e; }
  },

  async deleteAvailability(token, id) {
    try {
      const res = await authFetch(`${API_BASE}/scheduling/availability/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.error || 'Failed'); }
      return await res.json();
    } catch(e) { logError('deleteAvailability', e); throw e; }
  },

  async loadAllocations(token, filters = {}) {
    try {
      const params = new URLSearchParams(filters);
      const qs = params.toString();
      const res = await authFetch(`${API_BASE}/scheduling/allocations${qs ? '?' + qs : ''}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      return data.rows || [];
    } catch(e) { logError('loadAllocations', e); return []; }
  },

  async suggestAllocations(token, filters = {}) {
    try {
      const params = new URLSearchParams(filters);
      const qs = params.toString();
      const res = await authFetch(`${API_BASE}/scheduling/allocations/suggest${qs ? '?' + qs : ''}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) { logError('suggestAllocations', e); return { candidates: [] }; }
  },

  async createAllocation(token, payload) {
    try {
      const res = await authFetch(`${API_BASE}/scheduling/allocations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.error || 'Failed'); }
      return await res.json();
    } catch(e) { logError('createAllocation', e); throw e; }
  },

  async updateAllocation(token, id, patch) {
    try {
      const res = await authFetch(`${API_BASE}/scheduling/allocations/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(patch)
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.error || 'Failed'); }
      const data = await res.json();
      return data.row;
    } catch(e) { logError('updateAllocation', e); throw e; }
  },

  async cancelAllocation(token, id) {
    try {
      const res = await authFetch(`${API_BASE}/scheduling/allocations/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.error || 'Failed'); }
      return await res.json();
    } catch(e) { logError('cancelAllocation', e); throw e; }
  },

  async loadSchedulingCalendar(token, filters = {}) {
    try {
      const params = new URLSearchParams(filters);
      const qs = params.toString();
      const res = await authFetch(`${API_BASE}/scheduling/calendar${qs ? '?' + qs : ''}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) { logError('loadSchedulingCalendar', e); return { anaesthetists: [], availability: [], allocations: [] }; }
  },

  // ───────────────────────────────────────────────────
  // AI GOVERNANCE — AI-02 deterministic self-check + AI-01 analyser (admin-only)
  // Server: server/governance-selfcheck.cjs + server/ai-analyser.cjs
  // ───────────────────────────────────────────────────

  // AI-02 — run the deterministic self-check now. Omit tier for the full report
  // (incl. record-level exceptions, for human review in the admin view); pass
  // { tier:'t0' } for the aggregate-only projection. persist:true snapshots it.
  async runSelfCheck(token, { tier, persist } = {}) {
    try {
      const params = new URLSearchParams();
      if (tier) params.set('tier', tier);
      if (persist) params.set('persist', '1');
      const qs = params.toString();
      const res = await authFetch(`${API_BASE}/governance/self-check${qs ? '?' + qs : ''}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) { logError('runSelfCheck', e); return null; }
  },

  async loadSelfCheckHistory(token, limit = 30) {
    try {
      const res = await authFetch(`${API_BASE}/governance/self-check/history?limit=${limit}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) { logError('loadSelfCheckHistory', e); return []; }
  },

  async getSelfCheckRun(token, id) {
    try {
      const res = await authFetch(`${API_BASE}/governance/self-check/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) { logError('getSelfCheckRun', e); return null; }
  },

  // AI-01 — analyser status / toggle / run / history (T0-only governance critique)
  async getAiAnalyserStatus(token) {
    try {
      const res = await authFetch(`${API_BASE}/governance/ai-analysis/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) { logError('getAiAnalyserStatus', e); return null; }
  },

  async toggleAiAnalyser(token) {
    try {
      const res = await authFetch(`${API_BASE}/governance/ai-analysis/toggle`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.error || 'Failed'); }
      return await res.json();
    } catch(e) { logError('toggleAiAnalyser', e); throw e; }
  },

  async runAiAnalysis(token, kind = 'governance') {
    try {
      const qs = kind ? `?kind=${encodeURIComponent(kind)}` : '';
      const res = await authFetch(`${API_BASE}/governance/ai-analysis${qs}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.error || ('HTTP ' + res.status)); }
      return await res.json();
    } catch(e) { logError('runAiAnalysis', e); throw e; }
  },

  async loadAiAnalysisHistory(token, limit = 20) {
    try {
      const res = await authFetch(`${API_BASE}/governance/ai-analysis/history?limit=${limit}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) { logError('loadAiAnalysisHistory', e); return []; }
  },

  async getAiAnalysis(token, id) {
    try {
      const res = await authFetch(`${API_BASE}/governance/ai-analysis/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    } catch(e) { logError('getAiAnalysis', e); return null; }
  },

};

export default APIStore;
